// Retro-patch <git-common-dir>/info/exclude (Avatar block: .claude/, AGENTS.md,
// CLAUDE.md, .gitnexus/) cho mọi repo trong manifest .claude/repos.json. Dùng
// bởi `doctor --fix` và `sync` (mỗi lần sync). Thay patch-all-repos-gitignore.ts
// (ticket #138 phase-02 — đích ghi đổi .gitignore tracked → info/exclude untracked).
import { readFile } from "node:fs/promises";
import { join } from "node:path";
import { pathExists } from "./filesystem-helpers.js";
import { AVATAR_MARKER_START, buildAvatarRepoGitignoreBlock } from "./gitignore-template-loader.js";
import { probeGitCli, resolveGitCommonDir } from "./resolve-git-common-dir.js";
import { readReposManifest } from "./workspace-repos-manifest-store.js";
import {
  CorruptedExcludeMarkerError,
  NotAGitRepoError,
  writeAvatarBlockToGitExclude,
} from "./write-avatar-block-to-git-exclude.js";

export interface PatchExcludeResult {
  // Repo mà SAU KHI hàm chạy xong, info/exclude CHẮC CHẮN chứa đúng 1 block
  // Avatar — KỂ CẢ repo không phải ghi gì vì nội dung đã đúng sẵn (no-op).
  // Đây là cổng chặn của self-heal phase-03 (onlyRepos: patched) — KHÔNG phải
  // "danh sách repo vừa bị ghi trong lần chạy này".
  patched: string[];
  missing: string[]; // repo trong manifest nhưng thiếu folder (chưa clone)
  skippedNotGitRepo: string[]; // resolveGitCommonDir → null (không phải git repo)
  corrupted: string[]; // exclude có marker hỏng (START mất/đảo END) — KHÔNG đụng file
  failed: { name: string; error: string }[];
  backups: { name: string; backupPath: string }[];
  // Outcome cấp-run: probeGitCli() chạy MỘT lần đầu hàm. ≠ "ok" → trả ngay,
  // KHÔNG loop repo, KHÔNG xếp repo nào vào skippedNotGitRepo (không đổ oan).
  gitOutcome: "ok" | "gitUnavailable" | "gitTooOld";
  // Version git ĐO ĐƯỢC (`probeGitCli().version`), để call site in CON SỐ thay
  // vì chỉ nói "quá cũ" — không có nó, user phải tự chạy `git --version` mới
  // biết máy mình đang ở đâu so với sàn 2.31. `undefined` ở ca `unavailable`
  // (không chạy được git ⇒ không có version) và ca version không parse được.
  gitVersion?: string;
}

function defaultBackupDir(workspaceRoot: string): string {
  return join(workspaceRoot, ".claude", "_backup");
}

// try/catch PER-REPO: 1 repo lỗi (fs permission/readonly, git output bất
// thường...) KHÔNG chặn repo còn lại + KHÔNG throw ra ngoài — fail-soft đúng
// nghĩa loop-resilience (giữ khế ước patch-all-repos-gitignore.ts cũ).
export async function patchAllReposGitExclude(
  workspaceRoot: string,
  opts?: { backupDir?: string },
): Promise<PatchExcludeResult> {
  const patched: string[] = [];
  const missing: string[] = [];
  const skippedNotGitRepo: string[] = [];
  const corrupted: string[] = [];
  const failed: { name: string; error: string }[] = [];
  const backups: { name: string; backupPath: string }[] = [];

  // probeGitCli() MỘT lần cho cả run. ≠ "ok" → trả ngay: máy thiếu/quá cũ git
  // phải in "git không chạy được", KHÔNG được in "Bỏ qua (không phải git
  // repo): [cả 4 repo]" — thông điệp đó sai sự thật.
  const probe = probeGitCli();
  if (probe.status !== "ok") {
    return {
      patched,
      missing,
      skippedNotGitRepo,
      corrupted,
      failed,
      backups,
      gitOutcome: probe.status === "unavailable" ? "gitUnavailable" : "gitTooOld",
      gitVersion: probe.version,
    };
  }

  const repos = await readReposManifest(workspaceRoot);
  const block = buildAvatarRepoGitignoreBlock();
  const backupDir = opts?.backupDir ?? defaultBackupDir(workspaceRoot);

  for (const r of repos) {
    const repoPath = join(workspaceRoot, r.path);
    if (!(await pathExists(repoPath))) {
      missing.push(r.name);
      continue;
    }
    try {
      const backupPath = await writeAvatarBlockToGitExclude(repoPath, block, { backupDir });
      // patched cho MỌI ca không throw — kể cả no-op (backupPath === null vì
      // nội dung đã đúng sẵn). Đừng "tối ưu" thành chỉ push khi thật sự có ghi.
      patched.push(r.name);
      if (backupPath !== null) backups.push({ name: r.name, backupPath });
    } catch (err) {
      if (err instanceof NotAGitRepoError) {
        skippedNotGitRepo.push(r.name);
      } else if (err instanceof CorruptedExcludeMarkerError) {
        corrupted.push(r.name);
      } else {
        // Bao gồm GitOutputUnexpectedError (git < 2.31 lọt qua probe, trả rác)
        // — KHÔNG map sang skippedNotGitRepo (đó là mislabel). Repo không vào
        // patched ⇒ phase-03 không strip .gitignore của nó ⇒ rào cũ được giữ.
        failed.push({ name: r.name, error: err instanceof Error ? err.message : String(err) });
      }
    }
  }

  return {
    patched,
    missing,
    skippedNotGitRepo,
    corrupted,
    failed,
    backups,
    gitOutcome: "ok",
    gitVersion: probe.version,
  };
}

// THUẦN (chỉ đọc): { missing, uncheckable } cho mọi repo trong manifest.
//   - repo thiếu folder → bỏ qua (chưa clone ≠ chưa ignore).
//   - probeGitCli() ≠ "ok", hoặc resolveGitCommonDir không resolve được (null
//     hoặc ném GitOutputUnexpectedError) → uncheckable (KHÔNG "bỏ qua" im
//     lặng — nếu không, doctor báo ✓ trong khi repo đang trần trụi).
//   - exclude thiếu marker → missing.
export async function findReposMissingAvatarExclude(
  workspaceRoot: string,
): Promise<{ missing: string[]; uncheckable: string[] }> {
  const missing: string[] = [];
  const uncheckable: string[] = [];
  const probe = probeGitCli();
  const repos = await readReposManifest(workspaceRoot);

  for (const r of repos) {
    const repoPath = join(workspaceRoot, r.path);
    if (!(await pathExists(repoPath))) continue;

    if (probe.status !== "ok") {
      uncheckable.push(r.name);
      continue;
    }

    let common: string | null;
    try {
      common = resolveGitCommonDir(repoPath);
    } catch {
      // GitOutputUnexpectedError — không kiểm được, KHÔNG đổ oan "not a git repo".
      uncheckable.push(r.name);
      continue;
    }
    if (common === null) {
      uncheckable.push(r.name);
      continue;
    }

    const excludePath = join(common, "info", "exclude");
    const hasBlock =
      (await pathExists(excludePath)) &&
      (await readFile(excludePath, "utf8")).includes(AVATAR_MARKER_START);
    if (!hasBlock) missing.push(r.name);
  }

  return { missing, uncheckable };
}
